<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section class="page-section q-mb-lg">
      <q-form ref="form" class="full-width">
        <div class="row q-col-gutter-md">
          <div class="col-4">
            <q-input
              v-model="formData.id"
              :rules="[(val) => !!val || 'Field is required']"
              outlined
              dense
              :readonly="isEditMode"
              label="Workflow name"
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
          <div class="col-4">
            <q-input
              v-model="formData.bpmnProcessId"
              :rules="[(val) => !!val || 'Field is required']"
              outlined
              dense
              label="BPMN Process ID"
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
          <div class="col-4">
            <q-select
              v-model="formData.securityType"
              :options="[
                { label: 'Private', value: 'private' },
                { label: 'Public', value: 'public' },
              ]"
              outlined
              dense
              emit-value
              map-options
              label="Security Type"
              class="bg-white full-width"
              @update:model-value="onFormChange"
            />
          </div>
        </div>
      </q-form>
    </section>

    <section class="page-section">
      <div class="flex items-center justify-between q-mb-md">
        <div class="flex items-center">
          <span>Dark theme</span>
          <q-toggle false-value="vs-dark" class="q-mx-sm" true-value="vs" v-model="theme" />
          <span>Light theme</span>
        </div>
        <div class="flex items-center">
          <span>2 Columns</span>
          <q-toggle v-model="singleColumn" class="q-mx-sm" />
          <span>1 Column</span>
        </div>
      </div>

      <div class="row q-col-gutter-md">
        <div :class="singleColumn ? 'col-12' : 'col-6'">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">Input Variables Schema</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor
                :theme="theme"
                v-model="formData.inputVariablesSchema"
                :height="singleColumn ? '400px' : '500px'"
                @update:model-value="onFormChange"
              />
            </q-card-section>
          </q-card>
        </div>

        <div :class="singleColumn ? 'col-12' : 'col-6'">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">Authorization Schema</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor
                :theme="theme"
                v-model="formData.authorizationSchema"
                :height="singleColumn ? '400px' : '500px'"
                @update:model-value="onFormChange"
              />
            </q-card-section>
          </q-card>
        </div>

        <div :class="singleColumn ? 'col-12' : 'col-6'">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">Sub Schemas</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor
                :theme="theme"
                v-model="formData.subSchemas"
                :height="singleColumn ? '400px' : '500px'"
                @update:model-value="onFormChange"
              />
            </q-card-section>
          </q-card>
        </div>

        <div :class="singleColumn ? 'col-12' : 'col-6'">
          <q-card flat bordered class="full-height">
            <q-card-section class="q-pb-none">
              <div class="text-subtitle1 text-weight-bold">UI Schemas</div>
            </q-card-section>
            <q-card-section>
              <JsonEditor
                :theme="theme"
                v-model="formData.uiSchemas"
                :height="singleColumn ? '400px' : '500px'"
                @update:model-value="onFormChange"
              />
            </q-card-section>
          </q-card>
        </div>
      </div>
    </section>

    <section v-if="validationErrors.length" class="page-section q-mt-lg">
      <div class="text-subtitle1 text-weight-bold text-negative q-mb-sm">Validation Errors:</div>
      <ul class="validation-errors">
        <li v-for="(error, index) in validationErrors" :key="index" class="text-negative">
          {{ error }}
        </li>
      </ul>
    </section>

    <div class="buttons row q-mt-lg">
      <div class="col-12 flex q-gutter-sm">
        <q-btn
          v-if="!isValidated"
          no-caps
          color="primary"
          :label="'Validate'"
          @click="validateForm"
          :loading="validating"
          :disabled="!isDirty || disableEditMessage"
        >
          <q-tooltip style="z-index: 99999;" v-if="disableEditMessage">{{ disableEditMessage }}</q-tooltip>
        </q-btn>

        <q-btn
          v-else
          color="primary"
          no-caps
          :label="isEditMode ? 'Update' : 'Create'"
          @click="sendData"
          :loading="saving"
        />
        <q-btn flat label="Cancel" no-caps @click="$router.push('/blueprints')" />
      </div>
    </div>
  </q-page>
</template>

<script>
import { isEqual } from 'lodash';
import { validateWorkFlow, createWorkFlow } from '../registrations/api/api';
import { getBlueprints } from './api/api';
import JsonEditor from './components/JsonEditor.vue';

export default {
  name: 'BlueprintPage',
  components: {
    JsonEditor,
  },
  data() {
    return {
      theme: 'vs-dark',
      singleColumn: false,
      formData: {
        id: undefined,
        bpmnProcessId: undefined,
        securityType: 'public',
        inputVariablesSchema: '{}',
        authorizationSchema: '{}',
        subSchemas: '{}',
        uiSchemas: '{}',
      },
      originalFormData: null,
      isValidated: false,
      validating: false,
      saving: false,
      validationErrors: [],
      disableEditMessage: undefined,
    };
  },
  computed: {
    blueprintId() {
      return this.$route.params.id;
    },
    isEditMode() {
      return !!this.blueprintId;
    },
    isDirty() {
      if (!this.originalFormData) return false;
      return !isEqual(this.formData, this.originalFormData);
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.loadBlueprint();
    } else {
      this.originalFormData = JSON.parse(JSON.stringify(this.formData));
    }
  },
  methods: {
    onFormChange() {
      this.isValidated = false;
      this.validationErrors = [];
    },
    validateJsonFields() {
      const errors = [];

      try {
        JSON.parse(this.formData.inputVariablesSchema);
      } catch (e) {
        errors.push('Input Variables Schema: Invalid JSON syntax');
      }

      try {
        JSON.parse(this.formData.authorizationSchema);
      } catch (e) {
        errors.push('Authorization Schema: Invalid JSON syntax');
      }

      try {
        JSON.parse(this.formData.subSchemas);
      } catch (e) {
        errors.push('Sub Schemas: Invalid JSON syntax');
      }

      try {
        JSON.parse(this.formData.uiSchemas);
      } catch (e) {
        errors.push('UI Schemas: Invalid JSON syntax');
      }

      return errors;
    },
    buildPayload() {
      const inputVariables = {
        bpmnProcessId: this.formData.bpmnProcessId,
        securityType: this.formData.securityType,
        inputVariablesSchema: JSON.parse(this.formData.inputVariablesSchema),
        authorizationSchema: JSON.parse(this.formData.authorizationSchema),
        subSchemas: JSON.parse(this.formData.subSchemas),
        uiSchemas: JSON.parse(this.formData.uiSchemas),
      };

      if (this.isEditMode) {
        inputVariables.uuid = this.formData.uuid;
      } else {
        inputVariables.id = this.formData.id;
      }

      return {
        workflowId: this.isEditMode ? 'engprt.update-blueprint' : 'engprt.create-blueprint',
        inputVariables,
      };
    },
    async validateForm() {
      const isFormValid = await this.$refs.form.validate();
      if (!isFormValid) {
        this.$notify('negative', 'Error', 'Please fill in all required fields');
        return;
      }

      const jsonErrors = this.validateJsonFields();
      if (jsonErrors.length) {
        this.validationErrors = jsonErrors;
        this.$notify('negative', 'Error', 'Invalid JSON in one or more schema fields');
        return;
      }

      this.validating = true;
      this.validationErrors = [];

      try {
        const payload = this.buildPayload();
        await validateWorkFlow(payload);

        this.isValidated = true;
        this.$notify('positive', 'Success', 'Validation successful. You can now save the blueprint.');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Validation failed';
        this.validationErrors = Array.isArray(errorMessage) ? errorMessage : [errorMessage];
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.validating = false;
      }
    },
    async sendData() {
      if (!this.isValidated) {
        this.$notify('warning', 'Warning', 'Please validate the form first');
        return;
      }

      this.saving = true;

      try {
        const payload = this.buildPayload();
        await createWorkFlow(payload);

        this.$notify('positive', 'Success', `Blueprint ${this.isEditMode ? 'updated' : 'created'} successfully`);
        this.$router.push('/blueprints');
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to save blueprint';
        this.$notify('negative', 'Error', errorMessage);
      } finally {
        this.saving = false;
      }
    },
    async loadBlueprint() {
      this.$showLoading();
      try {
        const response = await getBlueprints(
          1,
          0,
          'createdAt',
          false,
          undefined,
          undefined,
          undefined,
          this.blueprintId,
        );
        const blueprint = response.data[0];
        this.formData = {
          id: blueprint.id,
          uuid: blueprint.uuid,
          bpmnProcessId: blueprint.bpmnProcessId,
          securityType: blueprint.securityType || 'public',
          inputVariablesSchema: JSON.stringify(blueprint.inputVariablesSchema, null, 2),
          authorizationSchema: JSON.stringify(blueprint.authorizationSchema, null, 2),
          subSchemas: JSON.stringify(blueprint.subSchemas, null, 2),
          uiSchemas: JSON.stringify(blueprint.uiSchemas, null, 2),
        };
        this.originalFormData = JSON.parse(JSON.stringify(this.formData));

        const {
          authorizationSchema,
          inputVariablesSchema,
          subSchemas,
          uiSchemas,
          uuid,
          securityType,
          bpmnProcessId,
        } = blueprint;
        const data = {
          workflowId: 'engprt.update-blueprint',

          inputVariables: {
            authorizationSchema,
            inputVariablesSchema,
            subSchemas,
            uiSchemas,
            securityType,
            uuid,
            bpmnProcessId,
          },
        };
        await validateWorkFlow(data).catch((res) => {
          this.disableEditMessage = res.message;
        });
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.$hideLoading();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-section {
  background-color: white;
  border-radius: 12px;
  padding: 16px;
}

.validation-errors {
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
  }
}
</style>
